/**
 * Field mapper for write-ops: maps user-supplied intent fields onto PISA
 * schema attribute keys, normalising case and validating against the schema.
 *
 * Design decisions (locked):
 * - Field key lookup uses lowercase normalisation on both sides
 * - Schema authority is Scrivito DataClass (via getSchemaAttributes) — never raw PISA
 * - Returns FieldMapping discriminated union (ok: true/false) from engine/types
 */

import { filterEnumByConstraints } from './codependent-enums'
import { findBestEnumMatch } from './enum-matcher'
import type {
  AdapterSchemaAttribute,
  AdapterSchemaAttributes,
} from '@/Atomic/Widgets/AiChatBot/core/engine/contracts'
import { FORCE_CURRENT_USER } from '@/Atomic/Widgets/AiChatBot/core/engine/entity-registry'
import { getHostRuntime } from '@/Atomic/Widgets/AiChatBot/core/engine/host-runtime'
import type {
  EntityClassName,
  FieldMapping,
  InvalidValueInfo,
  MissingFieldInfo,
} from '@/Atomic/Widgets/AiChatBot/core/engine/types'
import { formatDateTime } from '@/Atomic/Widgets/AiChatBot/core/tools/shared/formatDateTime'
import { getVisibleAttributes } from '@/Atomic/Widgets/AiChatBot/core/tools/shared/schema-cache'
import { getLocalizedSchemaEnumValues } from '@/Atomic/Widgets/AiChatBot/core/tools/shared/schemaLocalization'

/**
 * Resolve a reference field's stored id to a display name.
 * Returns the name when the record exists (falling back to the id if the record
 * has no title), or null when no target class holds the id — i.e. the reference
 * is dangling (a stale or fabricated id). Callers use null to reject the value
 * rather than persist a broken reference or show a raw id on the card.
 */
async function resolveReferenceDisplayName(
  referenceTo: string,
  id: string,
): Promise<string | null> {
  // "Contact" is a PISA generic type that maps to either Company or Person.
  // The adapter resolves the candidate classes so this stays free of CRM's split.
  const { data } = getHostRuntime()
  const candidates = data.resolveReferenceCandidateClasses(referenceTo)
  let found = false
  for (const className of candidates) {
    try {
      // adapter.get returns null for an unknown class or a missing record — both
      // mean "not this candidate", so skip. Narrowed to the .get() accessor to read
      // displayTitle (mirrors the adapter resolve + load pattern used elsewhere).
      const item = (await data.get(className, id)) as {
        get: (f: string) => unknown
      } | null
      if (!item) continue
      found = true
      const display = item.get('displayTitle')
      if (typeof display === 'string' && display.length > 0) return display
    } catch {
      // try next candidate
    }
  }
  return found ? id : null
}

export interface MapFieldsOptions {
  entityType: EntityClassName
  /** Raw field → value pairs from user intent (keys may be user-cased, e.g. "Priority"). */
  intentParams: Record<string, string>
  /**
   * Whether the mapping is for a create or an update. Controls the readonly gate:
   * PISA `readonly: true` means "write-once at creation, immutable after". On create,
   * write-once mandatory fields (e.g. Activity `type`) must pass through and are required;
   * on update they must be rejected. Mirrors the create/edit drawer's `readonly && !required`
   * skip rule (src/utils/CRUD/read/dataClass/index.ts).
   */
  operation: 'create' | 'update'
  /**
   * The record being updated (Scrivito DataItem accessor). On update, its current
   * controlling-field codes (group/type/subType) seed the enum resolver so a
   * co-dependent label resolves to the RIGHT code: many codes share a title
   * (e.g. "active" exists for every group), so without the record's group the
   * label→code match is ambiguous and would write an arbitrary same-title code.
   * On create there is no record — the controlling value comes from this turn's intent.
   */
  record?: { get: (field: string) => unknown }
  /**
   * requireTogether groups from the registry (e.g. Activity beginAt/endAt). A group of
   * exactly two datetime fields is treated as an ordered [start, end] pair so the end can
   * be derived from a supplied `durationMinutes` and a non-positive span is rejected —
   * the model must never do datetime arithmetic itself (it produced endAt == beginAt).
   */
  fieldGroups?: Array<{ requireTogether: string[] }>
  /**
   * The acting user's id (ctx.currentUserId). Lets a reference field whose value is
   * the FORCE_CURRENT_USER sentinel ("responsible is me") resolve to this id, the same
   * way relate_entity's targetId and forceAtCreate owner fields do. When absent, a
   * sentinel value is reported as an unresolved reference rather than written literally.
   */
  currentUserId?: string
}

/**
 * Map user-supplied field values onto PISA schema attribute keys.
 *
 * Steps:
 * 1. Load schema attributes via getSchemaAttributes (Scrivito).
 * 2. Build a lowercase → canonical-key lookup from the schema.
 * 3. For each intentParam key:
 *    a. Normalise to lowercase.
 *    b. Look up in the schema (exact then startsWith).
 *    c. If found and not readonly: add to mapped.
 *    d. If found but readonly, or not found: add to invalid.
 * 4. Check schema required fields against mapped — any required field
 *    not supplied goes to missing.
 * 5. Return ok: true with mapped / missing / invalid, or ok: false on schema error.
 *
 * The readonly flag from the Scrivito schema is the sole edit gate — there is no
 * separate editableFields allowlist.
 */
export async function mapFieldsFromIntent(
  options: MapFieldsOptions,
): Promise<FieldMapping> {
  const {
    entityType,
    intentParams,
    operation,
    record,
    fieldGroups,
    currentUserId,
  } = options

  const { data } = getHostRuntime()
  let schemaAttributes: AdapterSchemaAttributes | undefined
  try {
    const schemaName = data.getSchemaName(entityType)
    if (!schemaName) {
      return {
        ok: false,
        error: 'invalid-entity-type',
        message: `No schema name found for entity type: ${entityType}`,
      }
    }
    const result = await data.getSchema(schemaName)
    // Restrict to fields the create/edit drawer shows — attributes listed in a
    // group's attributeOrder. System fields absent from every group (e.g. Company
    // `isExternal`, which is mandatory + readonly but server-set) must not be
    // surfaced to the user. Mirrors schema-cache's getVisibleAttributes filter.
    const { visibleAttributes } = getVisibleAttributes(
      result.schemaGroups,
      result.groupsOrder,
      result.schemaAttributes,
    )
    if (visibleAttributes.length > 0) {
      const visible: AdapterSchemaAttributes = {}
      for (const name of visibleAttributes) {
        const attr = result.schemaAttributes[name]
        if (attr) {
          visible[name] = attr
        }
      }
      schemaAttributes = visible
    } else {
      schemaAttributes = result.schemaAttributes
    }
  } catch {
    return {
      ok: false,
      error: 'schema-unavailable',
      message: `Failed to load schema for ${entityType}`,
    }
  }

  if (!schemaAttributes || Object.keys(schemaAttributes).length === 0) {
    return {
      ok: false,
      error: 'schema-unavailable',
      message: `Schema for ${entityType} is empty or unavailable`,
    }
  }

  // Build lowercase → canonical key map from schema keys
  const schemKeysByLower = new Map<string, string>()
  // Also build localized title → canonical key map (e.g. "title" → "keyword")
  const titleByLower = new Map<string, string>()
  for (const [key, attr] of Object.entries(schemaAttributes)) {
    schemKeysByLower.set(key.toLowerCase(), key)
    const schemaAttr = attr as AdapterSchemaAttribute
    if (schemaAttr.title) {
      titleByLower.set(schemaAttr.title.toLowerCase(), key)
    }
  }

  // Every field that controls another field's enum options (e.g. `group` controls
  // `status`; Activity `type` controls `subType`, which controls `status`).
  const controllingKeys = new Set<string>()
  for (const attr of Object.values(schemaAttributes)) {
    for (const enumValue of (attr as AdapterSchemaAttribute).enumValues ?? []) {
      for (const key of Object.keys(enumValue.constraints ?? {})) {
        controllingKeys.add(key)
      }
    }
  }

  // Resolve the controlling values that pre-filter co-dependent enums to the RIGHT
  // code (many codes share a title — "active" exists for every group). Seed from the
  // caller (record codes on update, where the record already carries group/subType/
  // type) then fold in any controlling field supplied in this turn's intent (the
  // create case, where there is no record). Iterate to a fixed point so a chain like
  // type → subType → status resolves regardless of intent order.
  const intentByCanonical = new Map<string, string>()
  for (const [intentKey, value] of Object.entries(intentParams)) {
    const canonical =
      schemKeysByLower.get(intentKey.toLowerCase()) ??
      titleByLower.get(intentKey.toLowerCase())
    if (canonical !== undefined) {
      intentByCanonical.set(canonical, value)
    }
  }
  // Seed from the record's current codes, but never for a controlling field the user
  // is changing this turn — that new value (resolved from intent below) must win.
  const knownControlling: Record<string, string> = {}
  if (record) {
    for (const key of controllingKeys) {
      if (intentByCanonical.has(key)) continue
      const current = record.get(key)
      if (typeof current === 'string' && current.length > 0) {
        knownControlling[key] = current
      }
    }
  }
  let changed = true
  while (changed) {
    changed = false
    for (const key of controllingKeys) {
      if (key in knownControlling) continue
      const rawValue = intentByCanonical.get(key)
      if (rawValue === undefined) continue
      const attr = schemaAttributes[key] as AdapterSchemaAttribute | undefined
      if (!attr?.enumValues) continue
      const localised =
        getLocalizedSchemaEnumValues(attr.enumValues) ?? attr.enumValues
      const candidates = filterEnumByConstraints(localised, knownControlling)
      const exactByCode = candidates.find(
        (e) => e.value.toLowerCase() === rawValue.toLowerCase().trim(),
      )
      const matchedTitle = findBestEnumMatch(
        rawValue,
        candidates.map((e) => e.title),
      )
      const code =
        exactByCode?.value ??
        candidates.find((e) => e.title === matchedTitle)?.value
      if (code !== undefined) {
        knownControlling[key] = code
        changed = true
      }
    }
  }

  const mapped: Record<string, unknown> = {}
  const displayMapped: Record<string, string> = {}
  const currentDisplayMap: Record<string, string> = {}
  const labelMap: Record<string, string> = {}
  const enumDisplayMap: Record<string, Record<string, string>> = {}
  const invalid: string[] = []
  const immutable: Array<{ key: string; label: string }> = []
  const invalidValues: InvalidValueInfo[] = []
  const unresolvedReferences: Array<{
    key: string
    label: string
    value: string
  }> = []

  // Synthetic, non-schema input: total minutes for a begin/end datetime pair. Lets the
  // model pass the duration the user stated (e.g. "24 minutes") instead of computing the
  // end datetime itself — see the datetime-pair handling after the loop.
  let durationMinutes: number | undefined

  for (const [intentKey, value] of Object.entries(intentParams)) {
    const lowerKey = intentKey.toLowerCase()

    if (lowerKey === 'durationminutes') {
      const parsed = Number(value)
      if (Number.isFinite(parsed) && parsed > 0) {
        durationMinutes = parsed
      }
      continue
    }

    // Exact lowercase match
    let canonicalKey = schemKeysByLower.get(lowerKey)

    // Localized title fallback (e.g. "title" → "keyword", "type" → "type")
    if (canonicalKey === undefined) {
      canonicalKey = titleByLower.get(lowerKey)
    }

    if (canonicalKey === undefined) {
      invalid.push(intentKey)
      continue
    }

    // Readonly gate — schema is the source of truth, same as the create/edit drawer.
    // On update, every readonly field is immutable and rejected. On create, write-once
    // fields (readonly + mandatory, e.g. Activity `type`) must pass through — PISA requires
    // them at creation and freezes them after — while server-set readonly fields
    // (readonly + optional) are still rejected. Mirrors the drawer's `readonly && !required`
    // skip rule (src/utils/CRUD/read/dataClass/index.ts).
    const schemaField = schemaAttributes[canonicalKey] as AdapterSchemaAttribute
    const readonlyRejected =
      operation === 'create'
        ? schemaField.readonly && !schemaField.mandatory
        : schemaField.readonly
    if (readonlyRejected) {
      // On update the field IS recognised — it's just frozen after creation (e.g.
      // Activity `type`). Reporting it as "not recognised" misleads both the model
      // (it retries with a different key) and the user. Track it separately so the
      // write tool can say the field can't be changed once the record exists.
      if (operation === 'update') {
        immutable.push({
          key: intentKey,
          label: schemaField.title ?? canonicalKey,
        })
      } else {
        invalid.push(intentKey)
      }
      continue
    }

    // getSchemaAttributes already runs getLocalizedSchemaAttributes, so attr.title is
    // the resolved localized label — use it directly, fall back to the canonical key.
    const attr = schemaAttributes[canonicalKey] as AdapterSchemaAttribute
    labelMap[canonicalKey] = attr.title ?? canonicalKey

    // Enum fields: resolve display label → PISA value code via fuzzy match
    if (
      (attr.type === 'enum' || attr.type === 'enumarray') &&
      attr.enumValues &&
      attr.enumValues.length > 0
    ) {
      const localisedAll =
        getLocalizedSchemaEnumValues(attr.enumValues) ?? attr.enumValues
      // Restrict to options valid for this record's controlling values (e.g. status
      // options for the record's `group`). Many codes share a title ("active" exists
      // for every group), so matching a label against the unfiltered set resolves to
      // an arbitrary same-title code and writes the wrong one. Filtering first means
      // exactly one "active" survives — the one for this record's group.
      const localised = filterEnumByConstraints(localisedAll, knownControlling)
      // Build code → localized title lookup for current-value display in update diffs
      enumDisplayMap[canonicalKey] = Object.fromEntries(
        localised.map((e) => [e.value, e.title]),
      )
      // Try matching against `value` codes first (exact), then against localized titles
      const valueCodes = localised.map((e) => e.value)
      const exactByCode = valueCodes.find(
        (v) => v.toLowerCase() === value.toLowerCase().trim(),
      )
      if (exactByCode !== undefined) {
        const matchedEnum = localised.find((e) => e.value === exactByCode)
        mapped[canonicalKey] = exactByCode
        displayMapped[canonicalKey] = matchedEnum?.title ?? exactByCode
        continue
      }
      // Fuzzy match against localized titles, resolve back to value code
      const titles = localised.map((e) => e.title)
      const matchedTitle = findBestEnumMatch(value, titles)
      if (matchedTitle !== null) {
        const matchedEnum = localised.find((e) => e.title === matchedTitle)
        mapped[canonicalKey] = matchedEnum?.value ?? matchedTitle
        displayMapped[canonicalKey] = matchedTitle
      } else {
        // No match — never pass the raw value through (it would leak a guessed
        // PSA_* code onto the card and into the write). Record it with the
        // allowed options so the model can re-ask with human-readable labels.
        invalidValues.push({
          key: canonicalKey,
          label: attr.title ?? canonicalKey,
          suppliedValue: value,
          options: localised.map((e) => ({ value: e.value, title: e.title })),
        })
      }
      continue
    }

    // Date/datetime: bare date → UTC midnight; datetime with time must already carry Z.
    // The system prompt injects browserTimezone and requires the model to produce UTC (Z).
    // Silently fixing a timezone-unaware datetime would shift times by the browser offset.
    if (attr.type === 'date' || attr.type === 'datetime') {
      const hasTime = /T\d{2}:\d{2}/.test(value)
      let normalised: string
      if (hasTime) {
        // Has time component — must end with Z (UTC); reject anything else
        if (!value.endsWith('Z')) {
          invalid.push(intentKey)
          continue
        }
        // Normalise HH:MMZ → HH:MM:00Z (length 17); longer values pass through
        normalised = value.length === 17 ? `${value.slice(0, -1)}:00Z` : value
      } else {
        // Bare date — treat as UTC midnight
        normalised = `${value}T00:00:00Z`
      }
      mapped[canonicalKey] = normalised
      // Card/success text shows a locale-aware, human-readable value; PISA still
      // receives the raw UTC ISO in `mapped`. Show the time only when the value
      // carried one: some "date"-typed fields (e.g. Activity beginAt/endAt) actually
      // store a time, and hiding it makes a 17:00–17:24 span read as two equal dates.
      displayMapped[canonicalKey] = formatDateTime(
        normalised,
        hasTime ? undefined : 'PP',
      )
      continue
    }

    // Reference field: resolve the supplied id to a display name AND validate it
    // points to a live record. A fabricated or stale id (e.g. one the model guessed
    // instead of taking from a search_entities result) must never be written — that
    // would persist a dangling reference and leak the raw id onto the confirmation
    // card. Reject it so the write tool errors and the model re-resolves via search.
    if (attr.type === 'reference' && attr.referenceTo) {
      // "@currentUser" means "the acting user" — resolve it to their id here so a
      // reference field like Responsible can be set to "me" without the model copying
      // a 64-char id or looking one up. Mirrors relate_entity's targetId sentinel and
      // forceAtCreate's FORCE_CURRENT_USER. When the id is unknown, treat it as an
      // unresolved reference so the write tool reports it rather than writing "@currentUser".
      let refValue = value
      if (value === FORCE_CURRENT_USER) {
        if (!currentUserId) {
          unresolvedReferences.push({
            key: canonicalKey,
            label: attr.title ?? canonicalKey,
            value,
          })
          continue
        }
        refValue = currentUserId
      }
      const resolvedName = await resolveReferenceDisplayName(
        attr.referenceTo,
        refValue,
      )
      if (resolvedName === null) {
        unresolvedReferences.push({
          key: canonicalKey,
          label: attr.title ?? canonicalKey,
          value: refValue,
        })
        continue
      }
      mapped[canonicalKey] = refValue
      displayMapped[canonicalKey] = resolvedName
      // Resolve the current reference's name too (by id), so an update diff reads
      // "Name → Name". A reference field's value from record.get() is the raw id
      // string (see getReferenceDataItem / resolveEntityReference) — resolve it to
      // a display name, else the left of the diff leaks that raw id.
      const current = record?.get(canonicalKey)
      const currentId =
        typeof current === 'string' && current.length > 0 ? current : undefined
      if (currentId) {
        currentDisplayMap[canonicalKey] =
          (await resolveReferenceDisplayName(attr.referenceTo, currentId)) ??
          currentId
      }
      continue
    }

    mapped[canonicalKey] = value
    displayMapped[canonicalKey] = value
  }

  // Datetime requireTogether pairs (e.g. Activity beginAt/endAt): derive the end from a
  // supplied duration and reject a non-positive span. A group of exactly two datetime
  // fields is an ordered [start, end] pair (by the registry's array order). The model set
  // endAt == beginAt when asked to compute the end itself, so the arithmetic is done here.
  const asIso = (value: unknown): string | undefined =>
    typeof value === 'string' && value.length > 0 ? value : undefined
  const datetimePairs = (fieldGroups ?? [])
    .map((g) => g.requireTogether)
    .filter(
      (keys) =>
        keys.length === 2 &&
        keys.every((k) => {
          // Activity beginAt/endAt are declared "date" in the schema but carry a time.
          const type = schemaAttributes?.[k]?.type
          return type === 'date' || type === 'datetime'
        }),
    )

  for (const [startKey, endKey] of datetimePairs) {
    if (!startKey || !endKey) {
      continue
    }
    const startIso = asIso(mapped[startKey]) ?? asIso(record?.get(startKey))
    if (!startIso) {
      continue
    }

    // Derive the end from the stated duration when no explicit end was mapped this turn.
    if (durationMinutes !== undefined && mapped[endKey] === undefined) {
      const endIso = new Date(
        new Date(startIso).getTime() + durationMinutes * 60_000,
      )
        .toISOString()
        .replace(/\.\d{3}Z$/, 'Z')
      mapped[endKey] = endIso
      displayMapped[endKey] = formatDateTime(endIso)
      labelMap[endKey] =
        schemaAttributes[endKey]?.title ?? labelMap[endKey] ?? endKey
    }

    // Guard: the end must be strictly after the start. Compare the mapped end (or the
    // record's current end on update) against the start.
    const endIso = asIso(mapped[endKey]) ?? asIso(record?.get(endKey))
    if (
      endIso !== undefined &&
      new Date(endIso).getTime() <= new Date(startIso).getTime()
    ) {
      return {
        ok: false,
        error: 'invalid-datetime-range',
        message: `The end time must be after the start time (${labelMap[startKey] ?? startKey} → ${labelMap[endKey] ?? endKey}). Pass durationMinutes (the length in minutes) so it can be computed, or a later end time — never set them equal.`,
      }
    }
  }

  // Determine missing required fields
  const missing: MissingFieldInfo[] = []
  for (const [key, attr] of Object.entries(schemaAttributes)) {
    const schemaAttr = attr as AdapterSchemaAttribute
    // On update, readonly fields are immutable and can't be supplied, so never flag them
    // as missing. On create, all mandatory fields are required — including write-once ones.
    const immutableOnThisOperation =
      operation === 'update' ? schemaAttr.readonly : false
    if (schemaAttr.mandatory && !immutableOnThisOperation && !(key in mapped)) {
      {
        const info: MissingFieldInfo = {
          key,
          label: schemaAttr.title ?? key,
          type: schemaAttr.type,
        }
        if (
          (schemaAttr.type === 'enum' || schemaAttr.type === 'enumarray') &&
          schemaAttr.enumValues &&
          schemaAttr.enumValues.length > 0
        ) {
          const localised =
            getLocalizedSchemaEnumValues(schemaAttr.enumValues) ??
            schemaAttr.enumValues
          info.options = localised.map((e) => ({
            value: e.value,
            title: e.title,
          }))
        }
        missing.push(info)
      }
    }
  }

  return {
    ok: true,
    mapped,
    displayMapped,
    currentDisplayMap,
    labelMap,
    enumDisplayMap,
    missing,
    invalid,
    immutable,
    invalidValues,
    unresolvedReferences,
  }
}
